/**
 * Login page: Microsoft SSO (Entra ID). The server handles the OIDC flow at
 * /auth/login and sends the user back to `returnTo` (the page they wanted).
 */
import { useLocation } from 'react-router';
import { buttonClass } from '../components/Button.js';
import { loginUrl } from '../lib/navigation.js';
import { useDocumentTitle } from '../lib/prefs.js';
import { devLoginAvailable } from '../lib/session.js';
import styles from './Auth.module.css';
import { AuthLayout } from './AuthLayout.js';

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 21 21" width="20" height="20" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const DEV_ROLES = [
  { as: 'admin', label: 'Beheerder' },
  { as: 'planner', label: 'Planner' },
  { as: 'viewer', label: 'Lezer' },
] as const;

export interface WelcomePageProps {
  /** The session of this tab ended (expired or access revoked). */
  expired?: boolean;
  /** ...and the browser is on its way to the Microsoft login. */
  redirecting?: boolean;
}

export function WelcomePage({ expired = false, redirecting = false }: WelcomePageProps) {
  useDocumentTitle('Inloggen');
  const location = useLocation();
  const path = `${location.pathname}${location.search}`;
  const returnTo = encodeURIComponent(path);

  return (
    <AuthLayout>
      <div className={styles.intro}>
        <h1 className={styles.title}>Planning opgravingsteam</h1>
        <p className="muted">Log in met je Transect Microsoft-account om je eigen planning, de projecten en het team te bekijken.</p>
      </div>

      {expired && (
        <p className={styles.notice} role="status">
          {redirecting ? 'Je sessie is verlopen. Je wordt doorgestuurd om opnieuw in te loggen…' : 'Je sessie is verlopen. Log opnieuw in om verder te gaan.'}
        </p>
      )}

      <a href={loginUrl(path)} className={buttonClass('primary', 'lg', styles.login)}>
        <MicrosoftLogo />
        Inloggen met Microsoft
      </a>

      {devLoginAvailable() && (
        <section className={styles.dev} aria-labelledby="dev-login-title">
          <h2 id="dev-login-title" className={styles.devTitle}>
            Ontwikkelomgeving – inloggen zonder Microsoft
          </h2>
          <div className={styles.devButtons}>
            {DEV_ROLES.map((r) => (
              <a key={r.as} href={`/auth/dev-login?as=${r.as}&returnTo=${returnTo}`} className={buttonClass('default', 'sm')}>
                {r.label}
              </a>
            ))}
          </div>
        </section>
      )}
    </AuthLayout>
  );
}
